import { COLORS } from './colors'
import { TContrastColorsForShades, TShadeColorsForStatuses } from './typings'
import { ensureElementStateColors } from './utils/ensureElementStateColors'

const DISABLED_COLOR = COLORS.g091

/**
 * default theme colors,
 * may be overridden by company styles,
 * divided into semantic sections:
 *
 * - bgColors:      colors for static backgrounds (cards, modals, sections, ...)
 * - activeColors:  colors for active elements (buttons, inputs, form elements, ...)
 * - specialColors: colors for other use cases (special key, like illustration, or more generic, like overlays)
 */
export const DEFAULT_THEME = {
  bgColors: {
    primary: COLORS.g098,
    secondary: COLORS.g094,

    company: COLORS.companyPrimary,
    contrasted: COLORS.g031,

    card: COLORS.white,
    cart: COLORS.g094,
    messages: COLORS.g094, // eg. addon category validation

    info: COLORS.blueLight,
    error: COLORS.redLight,
    success: COLORS.greenLight,
    alert: COLORS.yellowLight,
  },

  activeColors: {
    // primary buttons, active elements, icons
    primary: ensureElementStateColors({
      color: COLORS.companyPrimary,
      defaultDisabledColor: DISABLED_COLOR,
    }),
    // secondary buttons
    secondary: ensureElementStateColors({
      color: COLORS.g088,
      defaultDisabledColor: DISABLED_COLOR,
    }),
    // categories, addon buttons, form elements, scroll to top btn, product filters
    tertiary: ensureElementStateColors({
      color: COLORS.white,
      defaultDisabledColor: DISABLED_COLOR,
    }),

    // buttons that lead to the next step in the order process
    conversion: ensureElementStateColors({
      color: COLORS.conversion,
      defaultDisabledColor: DISABLED_COLOR,
    }),

    // form elements on primary BG
    inputOnPrimary: ensureElementStateColors({
      color: COLORS.white,
      defaultDisabledColor: DISABLED_COLOR,
    }),
    // form elements on card BG
    inputOnCard: ensureElementStateColors({
      color: COLORS.g098,
      defaultDisabledColor: DISABLED_COLOR,
    }),

    info: ensureElementStateColors({ color: COLORS.blue, defaultDisabledColor: DISABLED_COLOR }),
    error: ensureElementStateColors({ color: COLORS.red, defaultDisabledColor: DISABLED_COLOR }),
    alert: ensureElementStateColors({ color: COLORS.yellow, defaultDisabledColor: DISABLED_COLOR }),
    success: ensureElementStateColors({
      color: COLORS.green,
      defaultDisabledColor: DISABLED_COLOR,
    }),
  },

  specialColors: {
    company: COLORS.companyPrimary,
    disabled: DISABLED_COLOR,
    illustration: COLORS.speedlo,

    mapElement: COLORS.black,
    mapElementFill: COLORS.white,
    mapElementHighlighted: COLORS.companyPrimary,

    overlayLite: COLORS.td055, // for company hero gradient
    overlayMedium: COLORS.td075, // for company hero gradient
    overlayHeavy: COLORS.td085, // for modal

    speedlo: COLORS.speedlo,
    speedloSecondary: COLORS.speedloSecondary,

    // feedback/rating
    star: COLORS.yellow,
    starEmpty: COLORS.g043,

    // status colors
    info: COLORS.blue,
    error: COLORS.red,
    success: COLORS.green,
    alert: COLORS.yellow,
  },
}

// * themed status colors (different sets for each shade)

export const STATUS_FG_COLORS: TShadeColorsForStatuses = {
  error: {
    dark: COLORS.red,
    light: COLORS.redLight,
  },
  success: {
    dark: COLORS.green,
    light: COLORS.greenLight,
  },
  info: {
    dark: COLORS.blue,
    light: COLORS.blueLight,
  },
  alert: {
    dark: COLORS.yellow,
    light: COLORS.yellowLight,
  },
  grey: {
    dark: COLORS.g018,
    light: COLORS.g098,
  },
}

// * themed fg colors (different sets for each shade)

/**
 * text or icon colors (use mainly these, not the ones from theme as those might get replaced with company colors)
 */
export const FG_COLORS: TContrastColorsForShades = {
  dark: {
    placeholder: COLORS.g075,
    low: COLORS.g043,
    normal: COLORS.g018,
    high: COLORS.black,
  },
  light: {
    placeholder: COLORS.g043,
    low: COLORS.g075,
    normal: COLORS.g094,
    high: COLORS.white,
  },
}

/**
 * divider colors (use mainly these, not the ones from theme as those might get replaced with company colors)
 */
export const FG_COLORS_TRANSPARENT: TContrastColorsForShades = {
  dark: {
    placeholder: COLORS.td010,
    low: COLORS.td010,
    normal: COLORS.td010,
    high: COLORS.td010,
  },
  light: {
    placeholder: COLORS.tl020,
    low: COLORS.tl020,
    normal: COLORS.tl020,
    high: COLORS.tl020,
  },
}
